import {
  getBoundingDates,
  requiredMessage,
} from '@components/CampaignWizard/validations';
import * as yup from 'yup';
import moment from 'moment';
import { Campaign } from '@local-types';

interface DateTimeValidationOptions {
  min: moment.Moment | null;
  max: moment.Moment | null;
}

const getAdGroupEarlyError = (minDateTime: moment.Moment | null) => {
  const formattedMinDateTime = moment(minDateTime).format(
    'MMM D, YYYY, h:mm A'
  );
  return `Ad Group dates cannot be set earlier than the Campaign's start date (${formattedMinDateTime})`;
};

const getAdGroupLateError = (maxDateTime: moment.Moment | null) => {
  const formattedMaxDateTime = moment(maxDateTime).format(
    'MMM D, YYYY, h:mm A'
  );
  return `Ad Group dates cannot be set later than the Campaign's end date (${formattedMaxDateTime})`;
};

export const getSchema = (alreadyStarted: boolean, campaign?: Campaign) =>
  yup
    .object()
    .shape(
      alreadyStarted ? getActiveSchema(campaign) : getInactiveSchema(campaign)
    );

const startDateTimeValidation = (
  endDateTimeFieldName: string,
  { min, max }: DateTimeValidationOptions
) =>
  yup
    .mixed()
    .required(requiredMessage)
    .test(
      'is-date',
      'Start date and time must be in a valid format',
      value => moment.isMoment(value) && value.isValid()
    )
    .test(
      'is-before-the-end-date',
      'Start date and time must be before the end date and time',
      function (value) {
        if (!this.parent[endDateTimeFieldName]) return true;
        return moment(value).isBefore(
          moment(this.parent[endDateTimeFieldName])
        );
      }
    )
    .test('in-bound-min', getAdGroupEarlyError(min), function (value) {
      if (!min) return true;
      return moment(value).isSameOrAfter(moment(min));
    })
    .test('in-bound-max', getAdGroupLateError(max), function (value) {
      if (!max) return true;
      return moment(value).isSameOrBefore(moment(max));
    });

const endDateTimeValidation = (
  startDateTimeFieldName: string,
  { min, max }: DateTimeValidationOptions
) =>
  yup
    .mixed()
    .nullable()
    .when({
      is: value => !!value,
      then: yup
        .mixed()
        .test(
          'is-date',
          'End date and time must be in a valid format',
          value => moment.isMoment(value) && value.isValid()
        )
        .test(
          'is-after-start-date',
          'End date and time must be after start date and time',
          function (value) {
            if (!this.parent[startDateTimeFieldName]) return true;
            return moment(value).isAfter(
              moment(this.parent[startDateTimeFieldName])
            );
          }
        )
        .test('in-bound-min', getAdGroupEarlyError(min), function (value) {
          if (!min) return true;
          return moment(value).isSameOrAfter(moment(min));
        })
        .test('in-bound-max', getAdGroupLateError(max), function (value) {
          if (!max) return true;
          return moment(value).isSameOrBefore(moment(max));
        }),
    });

const activeEndDateTimeValidation = ({
  min,
  max,
}: DateTimeValidationOptions) =>
  yup
    .mixed()
    .nullable()
    .when({
      is: value => !!value,
      then: yup
        .mixed()
        .test(
          'is-date',
          'End date and time must be in a valid format',
          value => moment.isMoment(value) && value.isValid()
        )
        .test('in-bound-min', getAdGroupEarlyError(min), function (value) {
          if (!min) return true;
          return moment(value).isSameOrAfter(moment(min));
        })
        .test('in-bound-max', getAdGroupLateError(max), function (value) {
          if (!max) return true;
          return moment(value).isSameOrBefore(moment(max));
        }),
    });

export const getActiveSchema = (campaign?: Campaign) => {
  const { minEndDate, maxEndDate } = getBoundingDates(
    campaign?.start_date,
    campaign?.end_date
  );

  return {
    startDateTime: yup.mixed().nullable(),
    endDateTime: activeEndDateTimeValidation({
      min: minEndDate,
      max: maxEndDate,
    }),
  };
};

export const getInactiveSchema = (campaign?: Campaign) => {
  const { minStartDate, maxStartDate, minEndDate, maxEndDate } =
    getBoundingDates(campaign?.start_date, campaign?.end_date);

  return {
    startDateTime: startDateTimeValidation('endDateTime', {
      min: minStartDate,
      max: maxStartDate,
    }),
    endDateTime: endDateTimeValidation('startDateTime', {
      min: minEndDate,
      max: maxEndDate,
    }),
  };
};
